import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import vipIconSvg from '@assets/img/icons/vip-icon.svg';
import { WebsocketEmitters } from '@common/app/socket';
import { socket } from '@common/socket/socket';
import { ModalContext } from '@components/Modals/Modal/config';
import { useAppDispatch, useAppSelector } from '@redux/store';

import { useLazyGetContractsQuery } from '@/api/queries/contract';
import { useLazyGetRewardsQuery } from '@/api/queries/rewards';
import {
  useLazyGetArenaOffersQuery,
  useShowPoputMutation,
} from '@/api/queries/shop';
import { useGetMyInfoMutation } from '@/api/queries/user';
import { firebaseLogEvent } from '@/firebase';
import {
  addToDiceCount,
  setDiceEnergy,
} from '@/redux/reducers/diceGameReducer';
import {
  changePaymentStatus,
  clearPendingPurchase,
  type PendingPurchase,
} from '@/redux/reducers/paymentsReducer';
import { changeProfitDropOffer } from '@/redux/reducers/shopReducer';
import {
  acitvateVipStatus,
  addToBalance,
  addToStorage,
  changeVipStatus,
} from '@/redux/reducers/userReducer';

import { ToastNotification } from '../components/ToastNotification/ToastNotification';
import { Playdeck, SendAnalitics } from '../lib/playdeck';
import { transformArenaOfferReward } from '../lib/transformArenaOfferReward';
import { type BoostItemType } from '../types/factoryType';
import { type AddFundsEventDataType } from '../types/payment';
import {
  type ActivateVipStatusResponseType,
  type ThankForPurchaseRequestResponseType,
} from '../types/query/user';
import { type ArenaOffersDto } from '../types/shop/marketBoxes';

import { useBuyBoostForCrypto } from './buy-boost-forCrypto';

import cupPng from '/img/cup.png';
import cupWebp from '/img/cup.webp';
import dicePng from '/img/dice-game/dice.png';
import diceWebp from '/img/dice-game/dice.webp';
import flashPng from '/img/dice-game/flash-huge.png';
import flashWebp from '/img/dice-game/flash-huge.webp';
import arrowUp from '/img/icons/arrow-up.svg';
import coinPng from '/img/tgem.png';
import coinWebp from '/img/tgem.webp';

const useAddFundsListener = (): void => {
  const addBoost = useBuyBoostForCrypto();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { isOpen, modalContent, secondModal, closeModal, openModal } =
    useContext(ModalContext);
  const [getShopInfo] = useLazyGetArenaOffersQuery();
  const [getMyInfo] = useGetMyInfoMutation();
  const [arenaShownPopup] = useShowPoputMutation();
  const [getRewards] = useLazyGetRewardsQuery();
  const [nextThanksModal, setNextThanksModal] =
    useState<ThankForPurchaseRequestResponseType | null>(null);
  const [getContracts] = useLazyGetContractsQuery();
  const pendingPurchase = useAppSelector(
    (state) => state.payment.pendingPurchase,
  );
  const pendingPurchaseRef = useRef<PendingPurchase | null>(pendingPurchase);

  useEffect(() => {
    pendingPurchaseRef.current = pendingPurchase;
  }, [pendingPurchase]);

  const firePurchaseSuccessfulFromPending = useCallback(
    (expectedItem: string): void => {
      const pending = pendingPurchaseRef.current;

      if (pending && pending.item === expectedItem) {
        SendAnalitics.purchaseSuccessfull({
          item: pending.item,
          item_id: pending.item_id,
          price: pending.price,
          currency: pending.currency,
        });
        dispatch(clearPendingPurchase());
      }
    },
    [dispatch],
  );

  const onPaidSuccessCallback = useCallback(
    (data: AddFundsEventDataType): void => {
      console.log('add funds');
      console.log(data);

      if (isOpen && secondModal.modalContent.renderKind === 'buySingleTgems')
        dispatch(changePaymentStatus('success'));
      firebaseLogEvent('purchase', {
        currency: 'USD',
        value: 0.013 * data.amountReceived,
        item_name: 'Tgem pack',
      });

      if (data.type === 'BuyTgems') {
        const pending = pendingPurchaseRef.current;
        const extras =
          pending && pending.item === 'tgem' ? pending.extras : null;

        dispatch(addToBalance({ tgem: data.amountCredited }));
        ToastNotification(
          `${t('Success!')} ${data.amountCredited} ${t('T-Gem credited and ready to use!')}`,
          { img: coinPng, webp: coinWebp },
        );
        firePurchaseSuccessfulFromPending('tgem');

        if (extras) {
          Playdeck.sendAnalitics({
            type: 'custom_event',
            name: 'tgem_purchase_completed',
            user_properties: {},
            event_properties: extras,
          });
        }
      }

      if (data.type === 'BuyDice') {
        dispatch(changePaymentStatus('success'));
        dispatch(addToDiceCount(data.amountCredited));
        ToastNotification(
          `${t('Success!')} ${data.amountCredited} ${t('Dice credited and ready to use!')}`,
          { img: dicePng, webp: diceWebp },
        );
        firePurchaseSuccessfulFromPending('dice');
      }

      if (data.type === 'BuyBingoBalls') {
        const pending = pendingPurchaseRef.current;
        const extras =
          pending && pending.item === 'bingo_balls' ? pending.extras : null;

        dispatch(changePaymentStatus('success'));
        dispatch(addToBalance({ bingoBalls: data.amountCredited }));
        ToastNotification(
          `${t('Success!')} ${data.amountCredited} ${t('Bingo balls credited and ready to use!')}`,
        );
        firePurchaseSuccessfulFromPending('bingo_balls');

        if (extras) {
          Playdeck.sendAnalitics({
            type: 'custom_event',
            name: 'bingo_purchase_completed',
            user_properties: {},
            event_properties: extras,
          });
        }
      }

      getMyInfo();
      // dispatch(authenticateUser());

      // if (secondModal.isOpen) {
      //   secondModal.closeModal();
      // }

      // if (isOpen) {
      //   closeModal();
      // }
    },
    [dispatch, t, secondModal.isOpen],
  );

  const onProductOfDayUpgrade = useCallback(async (): Promise<void> => {
    await getContracts().unwrap();
    dispatch(changePaymentStatus('success'));
    ToastNotification(t('Successfully upgraded'), {
      img: arrowUp,
    });
    firePurchaseSuccessfulFromPending('upgrade product of day');
  }, [dispatch, t, isOpen, firePurchaseSuccessfulFromPending]);

  const onVipActivatedSuccessCallback = useCallback(
    (data: ActivateVipStatusResponseType): void => {
      console.log('vip activated');
      console.log(data);

      if (isOpen && modalContent.renderKind === 'buyVip')
        dispatch(changePaymentStatus('success'));
      dispatch(acitvateVipStatus(data));
      dispatch(changeVipStatus(true));
      ToastNotification(t('Success. Your VIP status activated'), {
        img: vipIconSvg,
      });
    },
    [dispatch, t, isOpen],
  );

  const onBoostActivatedSuccessCallback = useCallback(
    (data: BoostItemType[]): void => {
      if (
        secondModal.isOpen &&
        secondModal.modalContent.renderKind === 'buyBoostX5X10'
      ) {
        setTimeout(() => {
          dispatch(changePaymentStatus('success'));
          ToastNotification(
            t(`Success buying Boost x${data[0].effectMultiplier}`),
          );
          addBoost(data);
        }, 1000);
      }
    },
    [dispatch, t, secondModal.isOpen, addBoost],
  );

  const onBuyArenaOfferSuccessCallback = useCallback(
    async (data: ArenaOffersDto): Promise<void> => {
      if (
        (isOpen || secondModal.isOpen) &&
        (modalContent.renderKind === 'buyArenaOffer' ||
          secondModal.modalContent.renderKind === 'buyArenaOffer')
      ) {
        dispatch(changePaymentStatus('success'));
      }

      const specialOffer = ['profit_drop_small', 'profit_drop_large'].includes(
        data.type,
      );

      await arenaShownPopup({
        leagueId: data.leagueId,
        type: data.type,
      });

      if (!specialOffer) {
        await getShopInfo();
        const { balancePayload, storagePayload } = transformArenaOfferReward(
          data.reward,
        );
        dispatch(addToBalance(balancePayload));
        dispatch(addToStorage(storagePayload));
      } else {
        console.log('profit drop reward');
        console.log(data);
        dispatch(changeProfitDropOffer(data));
      }

      if (
        secondModal.isOpen &&
        secondModal.modalContent.renderKind === 'buyArenaOffer'
      )
        closeModal();
      ToastNotification(t('Success buying Arena Offer'), {
        img: cupPng,
        webp: cupWebp,
      });
    },
    [dispatch, t, isOpen, secondModal.isOpen],
  );

  const onThankYouWindowCallback = useCallback(
    (data: ThankForPurchaseRequestResponseType) => {
      const modalData = {
        renderKind: 'thanksForPurchase',
        props: data,
      } as const;

      if (!modalContent.renderKind) {
        setNextThanksModal(null);

        return openModal(modalData);
      }
      setNextThanksModal(data);
    },
    [modalContent, secondModal.modalContent],
  );

  const onUpdateBoardGameEnergy = useCallback((data: number) => {
    ToastNotification(t('Game energy restore to full'), {
      img: flashPng,
      webp: flashWebp,
    });
    dispatch(setDiceEnergy(data));
  }, []);

  const handlePaidPlanStatus = useCallback(() => {
    dispatch(changePaymentStatus('success'));
    getRewards();
  }, [dispatch, getRewards]);

  const onPaidSuccessCallbackRef = useRef(onPaidSuccessCallback);
  const onProductOfDayUpgradeRef = useRef(onProductOfDayUpgrade);
  const onVipActivatedSuccessCallbackRef = useRef(
    onVipActivatedSuccessCallback,
  );
  const onBoostActivatedSuccessCallbackRef = useRef(
    onBoostActivatedSuccessCallback,
  );
  const onBuyArenaOfferSuccessCallbackRef = useRef(
    onBuyArenaOfferSuccessCallback,
  );
  const onThankYouWindowCallbackRef = useRef(onThankYouWindowCallback);
  const onUpdateBoardGameEnergyRef = useRef(onUpdateBoardGameEnergy);
  const handlePaidPlanStatusRef = useRef(handlePaidPlanStatus);

  useEffect(() => {
    onPaidSuccessCallbackRef.current = onPaidSuccessCallback;
  }, [onPaidSuccessCallback]);
  useEffect(() => {
    onProductOfDayUpgradeRef.current = onProductOfDayUpgrade;
  }, [onProductOfDayUpgrade]);
  useEffect(() => {
    onVipActivatedSuccessCallbackRef.current = onVipActivatedSuccessCallback;
  }, [onVipActivatedSuccessCallback]);
  useEffect(() => {
    onBoostActivatedSuccessCallbackRef.current =
      onBoostActivatedSuccessCallback;
  }, [onBoostActivatedSuccessCallback]);
  useEffect(() => {
    onBuyArenaOfferSuccessCallbackRef.current = onBuyArenaOfferSuccessCallback;
  }, [onBuyArenaOfferSuccessCallback]);
  useEffect(() => {
    onThankYouWindowCallbackRef.current = onThankYouWindowCallback;
  }, [onThankYouWindowCallback]);
  useEffect(() => {
    onUpdateBoardGameEnergyRef.current = onUpdateBoardGameEnergy;
  }, [onUpdateBoardGameEnergy]);
  useEffect(() => {
    handlePaidPlanStatusRef.current = handlePaidPlanStatus;
  }, [handlePaidPlanStatus]);

  useEffect(() => {
    const handlers = {
      thankYouWindow: (data: ThankForPurchaseRequestResponseType): void =>
        onThankYouWindowCallbackRef.current(data),
      addFund: (data: AddFundsEventDataType): void =>
        onPaidSuccessCallbackRef.current(data),
      vipActivated: (data: ActivateVipStatusResponseType): void =>
        onVipActivatedSuccessCallbackRef.current(data),
      boostActivated: (data: BoostItemType[]): void =>
        onBoostActivatedSuccessCallbackRef.current(data),
      buyArenaOffer: (data: ArenaOffersDto): Promise<void> =>
        onBuyArenaOfferSuccessCallbackRef.current(data),
      paidPlan: (): void => handlePaidPlanStatusRef.current(),
      productOfDayUpdated: (): Promise<void> =>
        onProductOfDayUpgradeRef.current(),
      updateBoardGameEnergy: (data: number): void =>
        onUpdateBoardGameEnergyRef.current(data),
    };

    socket.on(
      WebsocketEmitters.THANK_YOU_WINDOW_REWARD,
      handlers.thankYouWindow,
    );
    socket.on(WebsocketEmitters.ADD_FUND, handlers.addFund);
    socket.on(WebsocketEmitters.VIP_ACTIVATED, handlers.vipActivated);
    socket.on(WebsocketEmitters.BOOST_ACTIVATED, handlers.boostActivated);
    socket.on(WebsocketEmitters.BUY_ARENA_OFFER, handlers.buyArenaOffer);
    socket.on(WebsocketEmitters.PAID_PLAN, handlers.paidPlan);
    socket.on(
      WebsocketEmitters.PRODUCT_OF_DAY_UPDATED,
      handlers.productOfDayUpdated,
    );
    socket.on(
      WebsocketEmitters.UPDATE_BOARD_GAME_ENERGY,
      handlers.updateBoardGameEnergy,
    );

    return (): void => {
      socket.off(
        WebsocketEmitters.THANK_YOU_WINDOW_REWARD,
        handlers.thankYouWindow,
      );
      socket.off(WebsocketEmitters.ADD_FUND, handlers.addFund);
      socket.off(WebsocketEmitters.VIP_ACTIVATED, handlers.vipActivated);
      socket.off(WebsocketEmitters.BOOST_ACTIVATED, handlers.boostActivated);
      socket.off(WebsocketEmitters.BUY_ARENA_OFFER, handlers.buyArenaOffer);
      socket.off(WebsocketEmitters.PAID_PLAN, handlers.paidPlan);
      socket.off(
        WebsocketEmitters.PRODUCT_OF_DAY_UPDATED,
        handlers.productOfDayUpdated,
      );
      socket.off(
        WebsocketEmitters.UPDATE_BOARD_GAME_ENERGY,
        handlers.updateBoardGameEnergy,
      );
    };
  }, []);

  useEffect(() => {
    if (
      (!modalContent.renderKind || modalContent.renderKind === 'default') &&
      nextThanksModal
    ) {
      openModal({
        renderKind: 'thanksForPurchase',
        props: nextThanksModal,
      });
      setNextThanksModal(null);
    }
  }, [nextThanksModal, modalContent]);
};

export { useAddFundsListener };
